import { radioCardVariants } from './radio-card.variants'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

const radioButtonVariants = cva(
  'flex aspect-square size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
  {
    variants: {
      state: {
        default: 'border-main-border-semiBold bg-main-background-card',
        hover: 'border-main-border-semiBold bg-main-background-card',
        selected: 'border-main-border-focus bg-main-background-card',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
)

const radioIndicatorVariants = cva('size-2 rounded-full', {
  variants: {
    state: {
      default: 'bg-transparent',
      hover: 'bg-transparent',
      selected: 'bg-main-foreground-accent',
    },
  },
  defaultVariants: {
    state: 'default',
  },
})

export type RadioCardProps<T> = Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> &
  VariantProps<typeof radioCardVariants> & {
    title: string
    description?: React.ReactNode
    titleBadge?: React.ReactNode
    value: T
    checked?: boolean
    onSelect?: (value: T) => void
    showRadioButton?: boolean
    titleSuffix?: React.ReactNode
  }

export function RadioCard<T>({
  className,
  state = 'default',
  title,
  description,
  titleBadge,
  value,
  checked = false,
  onSelect,
  showRadioButton = true,
  titleSuffix,
  ...props
}: RadioCardProps<T>) {
  const handleClick = () => onSelect?.(value)

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick()
    }
  }

  const visualState: VariantProps<typeof radioCardVariants>['state'] = checked ? 'selected' : state

  return (
    <div
      role='radio'
      aria-checked={checked}
      tabIndex={0}
      className={cn(
        radioCardVariants({ state: visualState }),
        description == null && 'pb-6',
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <div className='flex flex-1 flex-col'>
        <div className='flex items-center gap-2.5'>
          {showRadioButton && (
            <div className={cn(radioButtonVariants({ state: visualState }))}>
              <div className={cn(radioIndicatorVariants({ state: visualState }))} />
            </div>
          )}
          <div className='flex items-center gap-1'>
            <h3 className='text-main-foreground-primary text-heading-xxsmall'>{title}</h3>
            {titleBadge}
          </div>
          {titleSuffix && <div className='ml-auto'>{titleSuffix}</div>}
        </div>
        {description != null && (
          <p
            className={cn(
              'text-main-foreground-primary text-body-default',
              showRadioButton && 'ml-6.5',
            )}
          >
            {description}
          </p>
        )}
      </div>
    </div>
  )
}
